import type { ComponentProps } from 'react';
import React, { useMemo } from 'react';
import { RegistrationFormUI } from 'src/components/AuthNew/components/RegistrationForm';
import type { FormValues } from 'src/components/AuthNew/components/RegistrationForm/types';
import type { CLIENT } from '@lt/api/dist/types/client';
import styled from 'styled-components';
import { setPhoneFormat } from 'src/components/AuthNew/utils';
import { ButtonWithLoader } from 'src/components/AuthNew/components/ButtonWithLoader/ButtonWithLoader';
import type { SetSharedState, SharedState } from 'src/components/AuthNew/types';
import { RequestStatus } from 'src/components/AuthNew/types';
import { useTranslation } from '@lt/localization';

const { Form, Content } = RegistrationFormUI;

type Props = {
  client: CLIENT;
  sharedState: SharedState;
  submitText?: string;
  isStyleAsMobile?: boolean;
  setSharedState: SetSharedState;
  backToLoginForm?: (value?: string) => void;
} & Pick<
  ComponentProps<typeof Form>,
  'onSubmit' | 'requestStatus' | 'isMobile'
>;

export const ClientUpdateForm = ({
  onSubmit,
  backToLoginForm,
  client,
  setSharedState,
  sharedState,
  requestStatus,
  isMobile,
  submitText,
  isStyleAsMobile,
}: Props) => {
  const { t } = useTranslation();
  const initialValues: FormValues = useMemo(
    () => ({
      id: client.id ?? undefined,
      name: client.name ?? '',
      surname: client.surname ?? '',
      email: client.email ?? '',
      phone: setPhoneFormat(sharedState.phone_mask ?? '', client.phone ?? ''),
      acceptProcessingPersonalData: false,
      acceptPromotions: true,
    }),
    [client, sharedState],
  );
  return (
    <Form
      onSubmit={onSubmit}
      initialValues={initialValues}
      HeaderComponent={() => <Title>{t('checkout.updateForm.title')}</Title>}
      ContentComponent={(props) => (
        <Content
          backToLoginForm={backToLoginForm}
          setSharedState={setSharedState}
          sharedState={sharedState}
          isStyleAsMobile={isStyleAsMobile}
          {...props}
        />
      )}
      FooterComponent={() => (
        <>
          <FooterWrapper>
            <ButtonWithLoader
              primary
              isLoading={requestStatus === RequestStatus.Submitting}
              type="submit"
              width={290}
            >
              {submitText || t('checkout.updateForm.footer')}
            </ButtonWithLoader>
          </FooterWrapper>
        </>
      )}
      requestStatus={requestStatus}
      isMobile={isMobile}
      validateOnMount
      id={client.id}
    />
  );
};

export const FooterWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 24px;
  text-align: center;
`;

export const Title = styled.h3`
  padding: 0 30px;
  font-size: 18px;
  font-style: normal;
  font-weight: 500;
  text-align: left;
`;
